/**
 * Active tenant context.
 *
 * The tenant id must be available to every page, and switching it has to do three
 * things together: re-scope the transport header, evict the previous tenant's
 * cached rows, and let pages re-render against the new scope. The selection and the
 * eviction both live in the session store (see `SessionStore.setTenant`), so there
 * is exactly one implementation of the switch; this component only projects the
 * store into React state and exposes the intent.
 *
 * The hooks that read this context live in `useTenant.ts`, so this module exports
 * only the provider and keeps React Fast Refresh working.
 */

import { useCallback, useMemo, type ReactNode } from 'react';

import { sessionStore, useSession } from './useSession';
import { TenantContext, type TenantContextValue } from './tenantContextValue';
import type { Tenant } from '../api/types';

export interface TenantProviderProps {
  readonly tenants: readonly Tenant[];
  readonly children: ReactNode;
}

export function TenantProvider({ tenants, children }: TenantProviderProps): JSX.Element {
  const session = useSession();

  const tenant = useMemo(
    () => tenants.find((entry) => entry.id === session.tenantId) ?? null,
    [tenants, session.tenantId],
  );

  const selectTenant = useCallback(
    (tenantId: string) => {
      // The store validates the id against this list, updates the value the
      // transport reads, and drops the previous tenant's cache entries.
      sessionStore.setTenant(tenantId, tenants);
    },
    [tenants],
  );

  const value = useMemo<TenantContextValue>(
    () => ({
      tenant,
      tenants,
      selectTenant,
      needsSelection: tenant === null && tenants.length > 0,
    }),
    [tenant, tenants, selectTenant],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
